const getApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__?.REACT_APP_API_URL) {
    return window.__RUNTIME_CONFIG__.REACT_APP_API_URL;
  }
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  return 'http://localhost:8000';
};

const getMediaBaseUrl = () => {
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__?.REACT_APP_MEDIA_URL) {
    return window.__RUNTIME_CONFIG__.REACT_APP_MEDIA_URL;
  }
  if (process.env.REACT_APP_MEDIA_URL) {
    return process.env.REACT_APP_MEDIA_URL;
  }
  return getApiBaseUrl();
};

/**
 * The `/media/...` path of an absolute URL, or null when it is not one of ours.
 *
 * Saved builds freeze `item.icon` at save time, so a build carries whichever
 * media host was current when it was saved — the frontend origin before the
 * R2 move (2026-08-12), `cdn.dofgen.com` after it. The frontend image no longer
 * ships `/media`, so the old hosts 404. Only the path is ever meaningful; the
 * host is the part that goes stale.
 */
const legacyMediaPath = (url) => {
  try {
    const parsed = new URL(url);
    if (!parsed.pathname.startsWith('/media/')) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
};

export const resolveMediaUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (/^(data:|blob:)/i.test(url)) return url;

  const path = /^https?:/i.test(url) ? legacyMediaPath(url) : (url.startsWith('/media/') ? url : null);
  if (path === null) return url;
  return `${getMediaBaseUrl().replace(/\/$/, '')}${path}`;
};

/**
 * Drop the host from any `/media/...` URL, deeply, so what we persist stays
 * host-free and cannot go stale the next time media moves. Returns the input
 * unchanged when there is nothing to rewrite, so callers can save the result
 * without copying whole payloads for nothing.
 */
export const relativizeMediaUrls = (value) => {
  if (typeof value === 'string') {
    if (!/^https?:/i.test(value)) return value;
    return legacyMediaPath(value) ?? value;
  }
  if (Array.isArray(value)) {
    let changed = false;
    const out = value.map((v) => {
      const next = relativizeMediaUrls(v);
      if (next !== v) changed = true;
      return next;
    });
    return changed ? out : value;
  }
  if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    let changed = false;
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      const next = relativizeMediaUrls(v);
      if (next !== v) changed = true;
      out[k] = next;
    }
    return changed ? out : value;
  }
  return value;
};
